import React, { useEffect, useMemo, useState, useRef } from 'react';
import { EllipsisIcon, SearchIcon, UserMinusIcon, UserPlusIcon } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { Customer, Payment, UserStatus } from '../data/demoData';
import { formatPrice } from '../data/pricingPlans';
type UsersPanelProps = {
  users: Customer[];
  payments: Payment[];
  onSelect: (user: Customer) => void;
  onStatusChange: (id: string, status: UserStatus) => void;
};
type QuickActionsMenuProps = {
  user: Customer;
  onStatusChange: (id: string, status: UserStatus) => void;
};
type UserFilter = UserStatus | 'All' | 'Payment failed' | 'Trial expired';
const filters: UserFilter[] = ['All', 'Waiting setup', 'Free trial', 'Active', 'Cancelled', 'Payment failed', 'Trial expired'];
export function UsersPanel({
  users,
  payments,
  onSelect,
  onStatusChange
}: UsersPanelProps) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<UserFilter>('All');
  const latestPayments = useMemo(() => Object.fromEntries(users.map(user => [user.id, getLatestPayment(payments.filter(payment => payment.userId === user.id))])), [payments, users]);
  const counts = useMemo(() => Object.fromEntries(filters.map(item => [item, item === 'All' ? users.length : item === 'Payment failed' ? users.filter(user => latestPayments[user.id]?.status === 'Failed').length : item === 'Trial expired' ? users.filter(hasExpiredTrial).length : users.filter(user => user.status === item).length])), [latestPayments, users]);
  const visibleUsers = users.filter(user => {
    const business = user.company?.name || user.tradingName || '';
    const latestPayment = latestPayments[user.id];
    return (filter === 'All' || (filter === 'Payment failed' ? latestPayment?.status === 'Failed' : filter === 'Trial expired' ? hasExpiredTrial(user) : user.status === filter)) && `${user.firstName} ${user.lastName} ${business}`.toLowerCase().includes(query.toLowerCase());
  });
  return <section aria-labelledby="users-title" data-id="element-471" data-mp="jbx80rz5oo2i">
      <div className="flex flex-col justify-between gap-4 border-b border-[#A6B6F8]/50 pb-6 lg:flex-row lg:items-end" data-id="element-472" data-mp="x8k833menucn">
        <div data-id="element-473" data-mp="s9po3ycpbzqy">
          <h1 id="users-title" className="text-3xl font-bold tracking-tight text-[#041F60]" data-id="element-474" data-mp="s9z5ixjokyl1">
            Users
          </h1>
          <p className="mt-2 text-sm text-stone-600" data-id="element-475" data-mp="11zshhznqjfpt">
            Manage onboarding, subscription, and payment follow-up.
          </p>
        </div>

        <div className="relative w-full lg:w-72" data-id="element-476" data-mp="1fxqnohrwk5u8">
          <label className="sr-only" htmlFor="user-search" data-id="element-477" data-mp="1b1rwynj3qbqn">
            Search users
          </label>
          <SearchIcon className="absolute left-3.5 top-3 text-stone-400" size={18} aria-hidden="true" data-id="element-478" data-mp="1unk2od52l16l" />
          <input id="user-search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search name or company" className="w-full rounded-lg border border-[#A6B6F8]/60 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-[#041F60] focus:ring-2 focus:ring-[#4BE4FF]" data-id="element-479" data-mp="1zj930iowym3y" />
        </div>
      </div>

      <div className="mt-5 flex gap-2 overflow-x-auto pb-1" aria-label="Filter users" data-id="element-480" data-mp="1gokcv25tmg8u">
        {filters.map(item => <button key={item} type="button" onClick={() => setFilter(item)} className={`whitespace-nowrap rounded-full px-3 py-2 text-xs font-bold transition ${filter === item ? item === 'Payment failed' ? 'bg-rose-700 text-white' : item === 'Trial expired' ? 'bg-amber-500 text-[#041F60]' : 'bg-[#041F60] text-white' : item === 'Payment failed' ? 'border border-rose-200 bg-white text-rose-700 hover:bg-rose-50' : item === 'Trial expired' ? 'border border-amber-200 bg-white text-amber-800 hover:bg-amber-50' : 'border border-[#A6B6F8]/60 bg-white text-[#3A3A3A] hover:border-[#5B5299]'}`} data-id="element-481" data-mp="16w659ss2afer">
            {item}{' '}
            <span className={`ml-1 ${filter === item ? item === 'Trial expired' ? 'text-[#041F60]' : 'text-[#4BE4FF]' : item === 'Payment failed' ? 'text-rose-400' : item === 'Trial expired' ? 'text-amber-500' : 'text-stone-400'}`} data-id="element-482" data-mp="74ssivk4xqgt">
              {counts[item]}
            </span>
          </button>)}
      </div>

      <div className="mt-5 overflow-visible rounded-xl border border-[#A6B6F8]/50 bg-white" data-id="element-483" data-mp="3pc9v0vs97qry">
        <div className="hidden grid-cols-[1.05fr_1.2fr_.85fr_.7fr_.8fr_20px_40px] gap-4 border-b border-[#A6B6F8]/50 bg-[#A6B6F8]/10 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-stone-500 lg:grid" data-id="element-484" data-mp="1rkdvl05pqg12">
          <span data-id="element-485" data-mp="35gp1d60u4uuw">Name</span>
          <span data-id="element-486" data-mp="3ag1vmbu1d47i">Business</span>
          <span data-id="element-487" data-mp="3af8pnd2wiiqf">Plan</span>
          <span data-id="element-488" data-mp="3aeh28pjgjrb8">Trial expiry</span>
          <span data-id="element-489" data-mp="3f6qn3r5pu16n">Account</span>
          <span className="sr-only" data-id="element-490" data-mp="3f6ibe2lbx1x1">Payment issue</span>
          <span className="sr-only" data-id="element-491" data-mp="3f94g7ugu53bk">Quick actions</span>
        </div>

        <div className="divide-y divide-stone-100" data-id="element-492" data-mp="3j7u3qfvl818x">
          {visibleUsers.map(user => {
          const latestPayment = latestPayments[user.id];
          return <article key={user.id} className="group grid gap-3 px-5 py-4 lg:grid-cols-[1.05fr_1.2fr_.85fr_.7fr_.8fr_20px_40px] lg:items-center lg:gap-4" data-id="element-493" data-mp="2rgea41vm5e97">
                <button type="button" onClick={() => onSelect(user)} className="text-left focus:outline-none focus:ring-2 focus:ring-[#4BE4FF]" data-id="element-494" data-mp="30ix6c34w8smh">
                  <span className="block text-sm font-bold text-[#041F60] group-hover:underline" data-id="element-495" data-mp="fq23ke7asazn">
                    {user.firstName} {user.lastName}
                  </span>
                  <span className="mt-0.5 block text-xs text-stone-500" data-id="element-496" data-mp="27ty4cxunz9ic">
                    {user.email}
                  </span>
                </button>

                <button type="button" onClick={() => onSelect(user)} className="text-left focus:outline-none focus:ring-2 focus:ring-[#4BE4FF]" data-id="element-497" data-mp="2lqla9q4lha0e">
                  <span className="block text-sm font-semibold text-[#041F60]" data-id="element-498" data-mp="1o5gyitp6bu6l">
                    {user.company?.name || user.tradingName}
                  </span>
                  <span className="mt-0.5 block text-xs text-stone-500" data-id="element-499" data-mp="22w6ahyagldbi">
                    {user.company ? `No. ${user.company.number}` : 'Sole trader'}
                  </span>
                </button>

                <div data-id="element-500" data-mp="15fhbsdgt2r6c">
                  <span className="mb-1 block text-xs font-semibold text-stone-500 lg:hidden" data-id="element-501" data-mp="15dwier689ea2">
                    Plan
                  </span>
                  <span className="block text-sm font-semibold text-[#041F60]" data-id="element-502" data-mp="3lsrfxg8hgqc6">
                    {user.planName}
                  </span>
                  <span className="mt-0.5 block text-xs text-stone-500" data-id="element-503" data-mp="382e9xjf5jlph">
                    {formatPrice(user.planPrice)} / month
                    {user.planDiscount && <span className="block font-semibold text-[#5B5299]" data-id="element-504" data-mp="3mmw9h2al9pa">
                        {user.planDiscount}
                      </span>}
                  </span>
                </div>

                <span className="text-sm text-stone-600" data-id="element-505" data-mp="3qsnntqkax1x0">
                  {user.trialExpiry || '—'}
                </span>

                <div data-id="element-506" data-mp="3vpe0s50t7he7">
                  <span className="mb-1 block text-xs font-semibold text-stone-500 lg:hidden" data-id="element-507" data-mp="3vss3jeddxsm6">
                    Account status
                  </span>
                  <StatusBadge status={user.status} data-id="element-508" data-mp="2kctman82009v" />
                </div>

                <PaymentHealth user={user} payment={latestPayment} data-id="element-509" data-mp="3b2dg94kljizq" />

                <QuickActionsMenu user={user} onStatusChange={onStatusChange} data-id="element-510" data-mp="3aziwzgdbk47w" />
              </article>;
        })}

          {!visibleUsers.length && <p className="px-5 py-12 text-center text-sm text-stone-500" data-id="element-511" data-mp="321pcr3stx113">
              No users match this search and filter.
            </p>}
        </div>
      </div>
    </section>;
}
function PaymentHealth({
  user,
  payment
}: {
  user: Customer;
  payment?: Payment;
}) {
  if (payment?.status === 'Failed') {
    return <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-xs font-black leading-none text-white" role="img" aria-label={`Payment failed on ${payment.date}`} title={`Payment failed on ${payment.date}`} data-id="element-512" data-mp="3l01maqnnkcx7">
        !
      </span>;
  }
  if (hasExpiredTrial(user)) {
    return <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-xs font-black leading-none text-[#041F60]" role="img" aria-label={`Free trial expired on ${user.trialExpiry}`} title={`Free trial expired on ${user.trialExpiry}`} data-id="element-513" data-mp="cinecv2azymi">
        !
      </span>;
  }
  return <span aria-label="No payment or trial issue" data-id="element-514" data-mp="ysnahhxjo67t" />;
}
function hasExpiredTrial(user: Customer) {
  if (user.status !== 'Free trial' || !user.trialExpiry) {
    return false;
  }
  const trialEnd = new Date(`${user.trialExpiry} 23:59:59`);
  return !Number.isNaN(trialEnd.getTime()) && trialEnd < new Date();
}
function QuickActionsMenu({
  user,
  onStatusChange
}: QuickActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = `user-actions-${user.id}`;
  useEffect(() => {
    function closeOnOutsideClick(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);
  function runAction(status: UserStatus) {
    onStatusChange(user.id, status);
    setIsOpen(false);
  }
  return <div className="relative justify-self-start lg:justify-self-end" ref={menuRef} data-id="element-515" data-mp="3tgu1zkizfcm5">
      <button type="button" onClick={() => setIsOpen(current => !current)} aria-expanded={isOpen} aria-haspopup="menu" aria-controls={menuId} aria-label={`Quick actions for ${user.firstName} ${user.lastName}`} className={`inline-flex h-9 min-w-9 items-center justify-center rounded-lg border px-2 outline-none transition focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2 ${isOpen ? 'border-[#041F60] bg-[#041F60] text-white' : 'border-[#A6B6F8]/60 bg-white text-[#5B5299] hover:border-[#5B5299] hover:bg-[#A6B6F8]/15 hover:text-[#041F60]'}`} data-id="element-516" data-mp="2clq667aw0yh">
        <EllipsisIcon size={19} aria-hidden="true" data-id="element-517" data-mp="1rt7jybqx05jg" />
        <span className="sr-only" data-id="element-518" data-mp="1mtuztyrfhckk">Quick actions</span>
      </button>

      {isOpen && <div id={menuId} role="menu" aria-label={`Quick actions for ${user.firstName} ${user.lastName}`} className="absolute right-0 z-20 mt-2 w-60 rounded-xl border border-[#A6B6F8]/60 bg-white p-1.5 shadow-lg" data-id="element-519" data-mp="2khd9rcwtskq5">
          <p className="px-2.5 pb-1.5 pt-1 text-xs font-semibold text-stone-500" data-id="element-520" data-mp="2ahck798d58di">
            Satago actions
          </p>

          <button type="button" role="menuitem" onClick={() => runAction('Active')} disabled={user.status === 'Active'} className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left outline-none transition enabled:hover:bg-[#A6B6F8]/15 focus:ring-2 focus:ring-[#4BE4FF] disabled:cursor-not-allowed disabled:opacity-50" data-id="element-521" data-mp="25mgo9y00a0v4">
            <UserPlusIcon size={16} className="mt-0.5 shrink-0 text-[#5B5299]" aria-hidden="true" data-id="element-522" data-mp="38cowjd18u8uw" />
            <span data-id="element-523" data-mp="3dai68g1r9kzx">
              <span className="block text-sm font-bold text-[#041F60]" data-id="element-524" data-mp="3ddw69hmy5i47">
                Set up user in Satago
              </span>
              <span className="mt-0.5 block text-xs leading-4 text-stone-500" data-id="element-525" data-mp="33ddem0p54xbq">
                {user.status === 'Active' ? 'User is already set up.' : 'Mark account active locally.'}
              </span>
            </span>
          </button>

          <div className="my-1 border-t border-[#A6B6F8]/50" data-id="element-526" data-mp="2uixn04niioz6" />

          <button type="button" role="menuitem" onClick={() => runAction('Cancelled')} disabled={user.status === 'Cancelled'} className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left outline-none transition enabled:hover:bg-rose-50 focus:ring-2 focus:ring-rose-200 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-527" data-mp="2fpc8szjcjv00">
            <UserMinusIcon size={16} className="mt-0.5 shrink-0 text-rose-700" aria-hidden="true" data-id="element-528" data-mp="2b1369haszlb1" />
            <span data-id="element-529" data-mp="2jzycuuhrexev">
              <span className="block text-sm font-bold text-rose-700" data-id="element-530" data-mp="2jzevpfzxod1w">
                Remove user from Satago
              </span>
              <span className="mt-0.5 block text-xs leading-4 text-stone-500" data-id="element-531" data-mp="2fycqmijc56s0">
                {user.status === 'Cancelled' ? 'User has already been removed.' : 'Cancel account locally.'}
              </span>
            </span>
          </button>
        </div>}
    </div>;
}
function getLatestPayment(payments: Payment[]) {
  return payments.reduce<Payment | undefined>((latest, payment) => {
    if (!latest) {
      return payment;
    }
    return new Date(payment.date).getTime() > new Date(latest.date).getTime() ? payment : latest;
  }, undefined);
}